/**
 * Utility functions for drag and drop calculations
 */

import { DragMoveEvent } from '@dnd-kit/core';
import { addMinutes } from 'date-fns';

export interface DragCalculationConfig {
    blockHeight: number;
    subBlockHeight: number;
    subBlockMinutes: number;
    subBlocks: number;
}

export interface DragPositionResult {
    date: Date | null;
    resizeHeight?: number;
    cursorType: string;
}

/**
 * Calculate the time position from a Y coordinate in the calendar
 */
export function calculateTimeFromY(
    y: number,
    config: DragCalculationConfig,
    baseDate: { year: number; month: number; day: number }
): Date {
    const { blockHeight, subBlocks } = config;

    // Calculate hour and minute from Y position
    const hour = Math.floor(y / blockHeight);
    const remainingPixels = y % blockHeight;
    const subBlock = Math.floor(remainingPixels / (blockHeight / subBlocks));
    const minute = subBlock * (60 / subBlocks);

    // Create the date
    return new Date(baseDate.year, baseDate.month, baseDate.day, hour, minute);
}

/**
 * Constrain a date/time to ensure it fits within calendar bounds
 */
export function constrainToCalendarBounds(
    startDate: Date,
    durationMinutes: number
): Date {
    const startHour = startDate.getHours();
    const startMinute = startDate.getMinutes();

    // Calculate end time
    const endTime = addMinutes(startDate, durationMinutes);
    const endHour = endTime.getHours();
    const endMinute = endTime.getMinutes();

    // If we've gone past midnight or into next day
    if (endTime.getDate() !== startDate.getDate() || endHour === 0) {
        // Calculate maximum start time for this duration
        const maxStartMinutes = (23 * 60 + 59) - durationMinutes;
        const maxHour = Math.floor(maxStartMinutes / 60);
        const maxMinute = maxStartMinutes % 60;

        // Constrain the start time
        const constrainedDate = new Date(startDate);
        constrainedDate.setHours(Math.min(startHour, maxHour));
        if (startHour === maxHour) {
            constrainedDate.setMinutes(Math.min(startMinute, maxMinute));
        }
        return constrainedDate;
    }

    // Ensure we don't start before midnight
    if (startHour < 0) {
        const constrainedDate = new Date(startDate);
        constrainedDate.setHours(0, 0, 0, 0);
        return constrainedDate;
    }

    return startDate;
}

/**
 * Calculate drag position for different drag types
 */
export function calculateDragPosition(
    event: DragMoveEvent,
    dragType: string,
    config: DragCalculationConfig & {
        planner: any;
        scrollOffset?: number;
    }
): DragPositionResult {
    const { planner, scrollOffset = 0 } = config;

    if (!event.over) {
        return { date: null, cursorType: 'default' };
    }

    // Handle resize operations
    if (dragType === 'calendar-item-resize-bottom') {
        const deltaY = event.delta.y;
        const subBlockChange = Math.round(deltaY / config.subBlockHeight);
        const newDuration = (planner?.duration || 30) + (subBlockChange * config.subBlockMinutes);
        const clampedDuration = Math.max(config.subBlockMinutes, Math.min(24 * 60, newDuration));
        const height = (clampedDuration / 60) * config.blockHeight;

        return {
            date: planner?.plannedAt ? new Date(planner.plannedAt) : null,
            resizeHeight: height,
            cursorType: 'ns-resize'
        };
    }

    if (dragType === 'calendar-item-resize-top') {
        const deltaY = -event.delta.y;
        const subBlockChange = Math.round(deltaY / config.subBlockHeight);
        const newDuration = (planner?.duration || 30) + (subBlockChange * config.subBlockMinutes);
        const clampedDuration = Math.max(config.subBlockMinutes, Math.min(24 * 60, newDuration));
        const minuteChange = -subBlockChange * config.subBlockMinutes;

        const newDate = planner?.plannedAt
            ? addMinutes(new Date(planner.plannedAt), minuteChange)
            : null;

        return {
            date: newDate,
            resizeHeight: (clampedDuration / 60) * config.blockHeight,
            cursorType: 'ns-resize'
        };
    }

    // Handle regular drag operations
    const evt = event.activatorEvent as PointerEvent;
    const dropZoneRect = event.over.rect;
    const baseDate = {
        year: event.over.data.current?.year || new Date().getFullYear(),
        month: event.over.data.current?.month || new Date().getMonth(),
        day: event.over.data.current?.day || new Date().getDate()
    };

    // Calculate mouse position relative to drop zone
    // Debug logging to understand the coordinate issue
    if (dragType === 'due-item') {
        console.log('=== DUE ITEM DRAG DEBUG ===');
        console.log('Initial click Y (evt.clientY):', evt.clientY);
        console.log('Drag delta Y:', event.delta.y);
        console.log('Current mouse Y (calculated):', evt.clientY + event.delta.y);
        console.log('DropZone top:', dropZoneRect.top);
        console.log('DropZone height:', dropZoneRect.height);
        console.log('scrollOffset passed in:', scrollOffset);

        // The real issue: dropZone.top should be negative when scrolled
        // If scrolled 171px, and the calendar starts at ~170px from viewport top,
        // then dropZone.top being -1 means it's nearly at the top of the viewport
        // This suggests the calendar column itself isn't scrolled, but its container is
        console.log('Expected dropZone.top if scrolled:', -scrollOffset + 170); // assuming calendar starts ~170px down
    }

    // Calculate mouse position relative to drop zone
    let relativeY = evt.clientY + event.delta.y - dropZoneRect.top;

    if (dragType === 'due-item' || dragType === 'unassigned-task') {
        // The visible position needs to account for scroll
        // The math: if we're at 461px relative to visible dropzone top,
        // but the calendar is scrolled down 171px, then we're actually at 461-171=290px in calendar time
        // This gives us ~4 hours which matches the visual position
        console.log('Before scroll adjustment:', relativeY);
        relativeY = relativeY - scrollOffset;
        console.log('After subtracting scroll offset:', relativeY);
    }

    if (dragType === 'due-item') {
        const estimatedHour = relativeY / config.blockHeight;
        console.log('Final relativeY:', relativeY);
        console.log('Estimated hour from position:', estimatedHour);
    }

    // For calendar items already in the calendar, subtract the initial click offset to prevent jumping
    if (dragType === 'calendar-item' && planner?.plannedAt) {
        const plannedStart = new Date(planner.plannedAt);
        const elementStartY = plannedStart.getHours() * config.blockHeight + (plannedStart.getMinutes() / 60) * config.blockHeight;
        const originalClickInCalendar = evt.clientY - dropZoneRect.top;
        const clickOffsetWithinElement = originalClickInCalendar - elementStartY;
        relativeY -= clickOffsetWithinElement;
    }

    // Calculate time from position
    let targetDate = calculateTimeFromY(relativeY, config, baseDate);

    if (dragType === 'due-item') {
        console.log('Final calculated date/time:', targetDate);
        console.log('=== END DUE ITEM DEBUG ===');
    }

    // Constrain to calendar bounds
    const duration = planner?.duration || 30;
    targetDate = constrainToCalendarBounds(targetDate, duration);

    return {
        date: targetDate,
        cursorType: 'grabbing'
    };
}

/**
 * Get drag type from active drag data
 */
export function getDragType(activeData: any): string {
    return activeData?.type || 'unknown';
}

/**
 * Check if a drag operation needs scroll offset compensation
 */
export function needsScrollOffset(dragType: string): boolean {
    return dragType === 'due-item' || dragType === 'unassigned-task';
}